
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function ThermalImagingElectricalInspectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Thermal Imaging Electrical Inspections',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Thermal imaging electrical inspections in Fresno, CA. Infrared technology detects hidden electrical problems, hot spots, and fire hazards before they fail.',
    url: `${siteUrl}/services/testing-certification/thermal-imaging-electrical-inspections`,
  };

  const features = [
    {
      icon: 'ri-flashlight-line',
      title: 'Infrared Technology',
      description:
        'Advanced thermal cameras detect temperature differences invisible to the naked eye',
    },
    {
      icon: 'ri-alert-line',
      title: 'Hot Spot Detection',
      description:
        'Identify overheating circuits, connections, and components before they fail or cause fires',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Hidden Problem Discovery',
      description:
        'Find electrical issues behind walls, in panels, and in inaccessible areas without opening them',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Fire Hazard Prevention',
      description:
        'Detect electrical problems that could cause fires before they become dangerous',
    },
    {
      icon: 'ri-tools-line',
      title: 'Loose Connection Identification',
      description:
        'Thermal imaging reveals loose connections that create heat and fire risks',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Overloaded Circuit Detection',
      description:
        'Identify circuits operating beyond safe capacity through heat signatures',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Non-Invasive Inspection',
      description:
        'Inspect electrical systems without opening walls or disrupting your property',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Thermal Image Report',
      description:
        'Detailed report with thermal images showing problem areas and temperature readings',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Professional thermal imaging camera',
    'Panel and breaker thermal scans',
    'Outlet and switch thermal testing',
    'Wiring thermal assessment',
    'Connection temperature analysis',
    'Hot spot identification',
    'Fire hazard detection',
    'Detailed thermal image report',
    'Repair recommendations and estimates',
  ];

  return (
    <>
      <SEOHead
        title="Thermal Imaging Electrical Inspections Fresno CA | Infrared Electrical Testing"
        description="Thermal imaging electrical inspections in Fresno, CA. Infrared technology detects hidden electrical problems, hot spots, and fire hazards. Licensed electricians available."
        canonical="/services/testing-certification/thermal-imaging-electrical-inspections"
        keywords="thermal imaging electrical inspection Fresno, infrared electrical inspection, thermal imaging testing, electrical hot spot detection"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Thermal Imaging Electrical Inspections — Fresno, CA"
        heroSubtitle="See hidden electrical problems with advanced infrared technology"
        heroImage="https://public.readdy.ai/ai/img_res/6a1e60c2716e09663fd2dce6561e9b57.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20thermal%20imaging%20camera%20display%20showing%20infrared%20heat%20signature%20of%20electrical%20panel%20with%20hot%20spots%20highlighted%20in%20red%20and%20orange%20colors%20next%20to%20actual%20electrical%20panel%20with%20circuit%20breakers%20visible%20bright%20professional%20setting&width=700&height=600&seq=thermal-fresno-001&orientation=portrait"
        fresnoContent="Fresno property owners use thermal imaging electrical inspections to find hidden problems before they cause fires or failures. Our licensed electricians use professional‑grade infrared cameras to detect overheating circuits, loose connections, overloaded panels, and electrical hazards invisible to the naked eye. Whether you're in Woodward Park, Tower District, or anywhere in Fresno County, thermal imaging provides early warning of electrical problems — allowing you to fix issues before they become dangerous or costly."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question:
              'What is thermal imaging electrical inspection and how does it work in Fresno?',
            answer:
              'Thermal imaging uses infrared cameras to detect heat signatures from electrical components. Overheating circuits, loose connections, overloaded breakers, and failing components show up as hot spots on the thermal image. This technology allows our Fresno electricians to find electrical problems behind walls, in panels, and in inaccessible areas without opening them — providing early warning of fire hazards and failures.',
          },
          {
            question: 'Why do I need thermal imaging electrical inspection in Fresno?',
            answer:
              'Thermal imaging detects electrical problems before they cause fires, power outages, or equipment damage. It finds issues that visual inspections and standard testing miss — like loose connections behind walls, overheating breakers, and overloaded circuits. Fresno homeowners use thermal imaging for pre‑purchase inspections, insurance requirements, preventive maintenance, and troubleshooting mysterious electrical problems.',
          },
          {
            question: 'How much does thermal imaging electrical inspection cost in Fresno?',
            answer:
              'Thermal imaging electrical inspections in Fresno typically cost $250–$500 depending on property size and inspection scope. Comprehensive whole‑house thermal scans cost more than targeted panel or circuit inspections. We provide upfront pricing and detailed reports with thermal images showing problem areas, temperature readings, and repair recommendations for your Fresno property.',
          },
          {
            question: 'What electrical problems can thermal imaging detect in Fresno homes?',
            answer:
              'Thermal imaging detects overheating circuit breakers, loose electrical connections, overloaded circuits, failing electrical components, undersized wiring, poor panel connections, damaged wiring behind walls, and electrical hot spots that indicate fire risks. Our Fresno electricians use thermal imaging to find problems that standard inspections miss — preventing fires and costly failures.',
          },
          {
            question: 'Is thermal imaging better than regular electrical inspections in Fresno?',
            answer:
              'Thermal imaging complements regular electrical inspections by revealing hidden problems. Visual inspections check visible components and test functionality, while thermal imaging detects heat signatures that indicate problems behind walls and inside panels. The combination provides the most comprehensive assessment. For older Fresno homes, homes with electrical issues, or pre‑purchase inspections, thermal imaging is highly recommended.',
          },
          {
            question: 'How long does thermal imaging electrical inspection take in Fresno?',
            answer:
              'Most residential thermal imaging inspections take 1–2 hours depending on home size and inspection scope. Comprehensive whole‑house thermal scans take longer than targeted panel or circuit inspections. We schedule inspections at your convenience and provide detailed reports with thermal images within 24–48 hours for Fresno property owners.',
          },
        ]}
      />
    </>
  );
}
